import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Zap, 
  Sun, 
  TrendingUp, 
  Users, 
  Leaf, 
  Home,
  Settings,
  LogOut,
  Plus,
  ArrowRight,
  Battery,
  Activity,
  Bell,
  HelpCircle,
  Shield,
  User,
  BarChart3,
  Radio
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { useTutorial } from '../components/Tutorial';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, needsMandate } = useAuth();
  const [stats, setStats] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const { showTutorial, setShowTutorial, resetTutorial, TutorialComponent } = useTutorial();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchDashboardData();
    fetchUnreadAlerts();
    // Poll for new alerts every 30 seconds
    const interval = setInterval(fetchUnreadAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadAlerts = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/alerts?unread_only=true`, { headers });
      setUnreadAlerts(response.data.length);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, communitiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { headers }),
        axios.get(`${API_URL}/api/my-communities`, { headers })
      ]);
      
      setStats(statsRes.data);
      setCommunities(communitiesRes.data);
      
      // Generate sample chart data if no real data
      generateChartData();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = () => {
    const data = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        production: Math.floor(Math.random() * 50) + 20,
        consumption: Math.floor(Math.random() * 40) + 15,
      });
    }
    setChartData(data);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
              <img src="/logo.png" alt="Green Teva" className="w-9 h-9 rounded-xl object-cover" />
              <span className="text-lg font-bold text-green-700 hidden sm:inline">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-1">
              <Link to="/dashboard" className="nav-link active">Dashboard</Link>
              <Link to="/communities" className="nav-link">Communautés</Link>
              <Link to="/forecasts" className="nav-link">Prévisions</Link>
              <Link to="/alerts" className="nav-link relative">
                Alertes
                {unreadAlerts > 0 && (
                  <span className="absolute -top-1 -right-2 px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full min-w-[16px] text-center font-medium">
                    {unreadAlerts}
                  </span>
                )}
              </Link>
              <Link to="/linky" className="nav-link">Linky</Link>
              <Link to="/simulator" className="nav-link">Simulateur</Link>
              {user?.role === 'admin' && (
                <Link to="/admin" className="nav-link flex items-center gap-1 !text-purple-600">
                  <Shield className="w-3.5 h-3.5" />
                  Admin
                </Link>
              )}
              {(user?.user_type === 'producer' || user?.role === 'pro') && (
                <Link to="/pro" className="nav-link flex items-center gap-1 !text-amber-600" data-testid="nav-pro">
                  <Sun className="w-3.5 h-3.5" />
                  Clients
                </Link>
              )}
              <Link to="/consumption" className="nav-link" data-testid="nav-consumption">Conso.</Link>
              <Link to="/live" className="nav-link flex items-center gap-1 !text-green-600" data-testid="nav-live">
                <Radio className="w-3 h-3 animate-pulse" />
                Live
              </Link>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="hidden sm:block text-right mr-1">
                <p className="text-sm font-medium text-foreground leading-tight">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.user_type === 'producer' ? 'Producteur' : 'Consommateur'}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/profile')}
                className="text-muted-foreground hover:text-green-700 hover:bg-green-50 h-9 w-9"
                data-testid="profile-btn"
                title="Mon profil"
              >
                <User className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={resetTutorial}
                className="text-muted-foreground hover:text-green-700 hover:bg-green-50 h-9 w-9"
                data-testid="help-btn"
                title="Aide"
              >
                <HelpCircle className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleLogout}
                className="text-muted-foreground hover:text-red-600 hover:bg-red-50 h-9 w-9"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Tutorial */}
      <TutorialComponent />

      {/* Main Content */}
      <main className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 pt-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1 tracking-tight">
              Bonjour, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              Voici un aperçu de votre activité énergétique
            </p>
          </div>

          {/* Stats Grid */}
          {needsMandate && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="mandate-banner">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800">Mandat Enedis requis</p>
                  <p className="text-xs text-amber-700">Signez votre mandat d'accès aux données Linky pour activer toutes les fonctionnalités.</p>
                </div>
              </div>
              <Button onClick={() => navigate('/mandat-enedis')} className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-5 py-2 text-sm font-semibold shrink-0" data-testid="mandate-banner-btn">
                Signer le mandat
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8" data-testid="stats-grid">
            <Card className="stats-card metric-glow" data-testid="stat-production">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center">
                    <Sun className="w-5 h-5 text-amber-500" />
                  </div>
                  <span className="text-[10px] font-medium text-muted-foreground bg-slate-100 px-2 py-0.5 rounded-full">
                    Ce mois
                  </span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-foreground mono">
                  {stats?.total_production_kwh?.toFixed(1) || '0'} <span className="text-sm font-medium text-muted-foreground">kWh</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">Production totale</p>
              </CardContent>
            </Card>

            <Card className="stats-card metric-glow" data-testid="stat-consumption">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <Battery className="w-5 h-5 text-blue-500" />
                  </div>
                  <span className="text-[10px] font-medium text-muted-foreground bg-slate-100 px-2 py-0.5 rounded-full">
                    Ce mois
                  </span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-foreground mono">
                  {stats?.total_consumption_kwh?.toFixed(1) || '0'} <span className="text-sm font-medium text-muted-foreground">kWh</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">Consommation totale</p>
              </CardContent>
            </Card>

            <Card className="stats-card metric-glow" data-testid="stat-savings">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-green-50 border border-green-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-[10px] font-medium text-green-600 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full">
                    +{stats?.self_consumption_rate || 0}%
                  </span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-foreground mono">
                  {stats?.savings_euros?.toFixed(2) || '0'} <span className="text-sm font-medium text-muted-foreground">€</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">Économies réalisées</p>
              </CardContent>
            </Card>

            <Card className="stats-card metric-glow" data-testid="stat-co2">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <Leaf className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-foreground mono">
                  {stats?.co2_saved_kg?.toFixed(1) || '0'} <span className="text-sm font-medium text-muted-foreground">kg</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">CO₂ évité</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts and Communities */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Energy Chart */}
            <Card className="lg:col-span-2 stats-card" data-testid="energy-chart">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">Production vs Consommation</CardTitle>
                  <Activity className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorProduction" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorConsumption" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={false}
                        unit=" kWh"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="production"
                        name="Production"
                        stroke="#22c55e"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorProduction)"
                      />
                      <Area
                        type="monotone"
                        dataKey="consumption"
                        name="Consommation"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorConsumption)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* My Communities */}
            <Card className="stats-card" data-testid="my-communities">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">Mes Communautés</CardTitle>
                  <Users className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                {communities.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground mb-4">
                      Vous n'avez pas encore rejoint de communauté
                    </p>
                    <Button 
                      onClick={() => navigate('/communities')}
                      className="btn-primary"
                      data-testid="find-community-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Trouver une communauté
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {communities.slice(0, 3).map((community) => (
                      <div 
                        key={community.id}
                        className="p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                        data-testid={`community-link-${community.id}`}
                      >
                        <Link to={`/communities/${community.id}`} className="block">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-foreground">{community.name}</p>
                              <p className="text-sm text-muted-foreground">{community.location}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-primary">{community.member_count} membres</p>
                              <p className="text-xs text-muted-foreground">{community.price_per_kwh} €/kWh</p>
                            </div>
                          </div>
                        </Link>
                        <div className="mt-2 flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs h-7 px-2"
                            onClick={async (e) => {
                              e.preventDefault();
                              if (!window.confirm(`Quitter la communauté "${community.name}" ?`)) return;
                              try {
                                const token = localStorage.getItem('token');
                                await axios.post(`${API_URL}/api/communities/${community.id}/leave`, {}, { headers: { Authorization: `Bearer ${token}` }});
                                toast.success('Vous avez quitté la communauté');
                                setCommunities(prev => prev.filter(c => c.id !== community.id));
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erreur');
                              }
                            }}
                            data-testid={`leave-community-${community.id}`}
                          >
                            Quitter
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {communities.length > 3 && (
                      <Button 
                        variant="ghost" 
                        className="w-full text-primary"
                        onClick={() => navigate('/communities')}
                        data-testid="view-all-communities-btn"
                      >
                        Voir toutes les communautés
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(user?.user_type === 'producer' || user?.role === 'pro') && (
              <Button 
                variant="outline" 
                className="h-auto p-6 flex flex-col items-center gap-3 hover:bg-primary/5 hover:border-primary"
                onClick={() => navigate('/pro')}
                data-testid="manage-clients-action"
              >
                <Users className="w-8 h-8 text-primary" />
                <span className="font-medium">Gérer mes clients</span>
              </Button>
            )}
            <Button 
              variant="outline" 
              className="h-auto p-6 flex flex-col items-center gap-3 hover:bg-primary/5 hover:border-primary"
              onClick={() => navigate('/consumption')}
              data-testid="consumption-action"
            >
              <BarChart3 className="w-8 h-8 text-primary" />
              <span className="font-medium">Ma consommation</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto p-6 flex flex-col items-center gap-3 hover:bg-primary/5 hover:border-primary"
              onClick={() => navigate('/communities/create')}
              data-testid="create-community-action"
            >
              <Plus className="w-8 h-8 text-primary" />
              <span className="font-medium">Créer une communauté</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto p-6 flex flex-col items-center gap-3 hover:bg-primary/5 hover:border-primary"
              onClick={() => navigate('/profile')}
              data-testid="profile-action"
            >
              <User className="w-8 h-8 text-primary" />
              <span className="font-medium">Mon profil</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto p-6 flex flex-col items-center gap-3 hover:bg-green-50 hover:border-green-400 border-green-200"
              onClick={() => navigate('/live')}
              data-testid="live-action"
            >
              <Radio className="w-8 h-8 text-green-600 animate-pulse" />
              <span className="font-medium text-green-700">Flux en direct</span>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
